/**
 * Client-side demo/sample data for testing the UI before real inventory
 * exists. Toggled on and off with one click (DemoToggle in the nav); the
 * setting persists in localStorage and never touches the API or D1.
 *
 * Everything is generated deterministically from (game_id, section) seeds, so
 * the map, the browse grid, and the section drill-down always agree with each
 * other. Demo listing ids are prefixed `demo-` and can never be purchased.
 */

import { useCallback, useEffect, useState } from 'react';
import type { Game, Listing, MapSection } from './api';
import { ALL_SECTION_IDS, levelOf, zoneOf, type LevelId, type Zone } from './metlife';

const KEY = 'nygtix_demo_data';
const EVT = 'nygtix-demo-change';

export function isDemoOn(): boolean {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

export function isDemoListing(listingId: string): boolean {
  return listingId.startsWith('demo-');
}

/** React hook — stays in sync across components (and browser tabs). */
export function useDemoMode(): [boolean, (on: boolean) => void] {
  const [on, setOnState] = useState(isDemoOn);
  useEffect(() => {
    const sync = () => setOnState(isDemoOn());
    window.addEventListener(EVT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  const set = useCallback((v: boolean) => {
    try {
      localStorage.setItem(KEY, v ? '1' : '0');
    } catch {
      /* private mode — toggle still works for this render via the event */
    }
    window.dispatchEvent(new Event(EVT));
  }, []);
  return [on, set];
}

// ---------------------------------------------------------------------------
// Deterministic PRNG
// ---------------------------------------------------------------------------

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function rng(seed: string): () => number {
  let a = hash(seed);
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---------------------------------------------------------------------------
// Sample games (used when the schedule is empty)
// ---------------------------------------------------------------------------

const DEMO_SCHEDULE: [opponent: string, date: string, kickoff: string, week: number][] = [
  ['Dallas Cowboys', '2026-09-13', '13:00', 1],
  ['Philadelphia Eagles', '2026-09-27', '13:00', 3],
  ['Green Bay Packers', '2026-10-11', '16:25', 5],
  ['Washington Commanders', '2026-10-25', '13:00', 7],
  ['Minnesota Vikings', '2026-11-08', '13:00', 9],
  ['Chicago Bears', '2026-11-22', '13:00', 11],
  ['Denver Broncos', '2026-12-06', '20:20', 13],
  ['Las Vegas Raiders', '2026-12-20', '13:00', 15],
];

export function demoGames(): Game[] {
  return DEMO_SCHEDULE.map(([opponent, game_date, kickoff_time, week_number]) => ({
    game_id: `demo-game-${week_number}`,
    season: '2026',
    opponent: `${opponent} (sample)`,
    game_date,
    kickoff_time,
    week_number,
    venue: 'MetLife Stadium, East Rutherford, NJ',
  }));
}

// ---------------------------------------------------------------------------
// Per-section availability profile
// ---------------------------------------------------------------------------

type Profile = {
  included: boolean;
  sectionType: string;
  listingCount: number;
  minPriceCents: number;
};

const ZONE_MULT: Record<Zone, number> = {
  'sideline-home': 1.7,
  'sideline-visitor': 1.55,
  'corner-nw': 1.25,
  'corner-ne': 1.25,
  'corner-se': 1.2,
  'corner-sw': 1.2,
  'endzone-west': 1.0,
  'endzone-east': 1.0,
};

const LEVEL_BASE: Record<LevelId, number> = { lower: 260, mezz: 170, upper: 95 };

function sectionType(level: LevelId, zone: Zone, r: number): string {
  const sideline = zone === 'sideline-home' || zone === 'sideline-visitor';
  const corner = zone.startsWith('corner');
  if (level === 'lower') {
    if (sideline) return r < 0.4 ? 'Field 1 Center' : 'Field 1';
    if (corner) return 'Field 2';
    return r < 0.5 ? 'Field 3 Front' : 'Field 3 Back';
  }
  if (level === 'mezz') {
    if (sideline) {
      return (['Mezz Club A', 'Mezz Club B', 'Mezz Club B1', 'Mezz Club C'] as const)[
        Math.floor(r * 4)
      ]!;
    }
    return corner ? 'Mezzanine Front' : 'Mezzanine Back';
  }
  if (sideline) return r < 0.2 ? 'Loge' : 'Terrace Center';
  return corner ? 'Terrace Corner' : 'Terrace End Zone';
}

function profile(gameId: string, section: string): Profile {
  const r = rng(`${gameId}|${section}`);
  const included = r() < 0.72;
  const level = levelOf(section) ?? 'upper';
  const zone = zoneOf(section) ?? 'endzone-west';
  const typeRoll = r();
  const type = sectionType(level, zone, typeRoll);
  let price = LEVEL_BASE[level] * ZONE_MULT[zone] * (0.85 + r() * 0.5);
  if (type.startsWith('Mezz Club')) price *= 2.1;
  if (type === 'Loge') price *= 1.4;
  return {
    included,
    sectionType: type,
    listingCount: 1 + Math.floor(r() * 4),
    minPriceCents: Math.round(price) * 100,
  };
}

/** Sections lit up on the stadium map for a game. */
export function demoSectionAvailability(gameId: string): MapSection[] {
  const out: MapSection[] = [];
  for (const id of ALL_SECTION_IDS) {
    const p = profile(gameId, id);
    if (!p.included) continue;
    out.push({
      section: id,
      section_type: p.sectionType,
      listing_count: p.listingCount,
      min_price_cents: p.minPriceCents,
    });
  }
  return out;
}

const NOTES = [
  'Aisle seats',
  'Great view of the tunnel',
  'Under the overhang — stays dry',
  'Season-ticket seats since 2010',
  'Selling at cost',
];

const ROW_MAX: Record<LevelId, number> = { lower: 45, mezz: 24, upper: 28 };

/** Listings for one section+game, consistent with demoSectionAvailability. */
export function demoListingsFor(gameId: string, section: string, game?: Game): Listing[] {
  const key = section.trim().toUpperCase();
  const p = profile(gameId, key);
  if (!p.included) return [];
  const g = game ?? demoGames().find((d) => d.game_id === gameId);
  const r = rng(`listings|${gameId}|${key}`);
  const level = levelOf(key) ?? 'upper';
  const out: Listing[] = [];
  for (let i = 0; i < p.listingCount; i++) {
    const qty = [2, 2, 2, 3, 4][Math.floor(r() * 5)]!;
    const seatStart = 1 + Math.floor(r() * 18);
    const seats = Array.from({ length: qty }, (_, k) => String(seatStart + k));
    const price = Math.round((p.minPriceCents / 100) * (1 + i * 0.08 + r() * 0.06)) * 100;
    out.push({
      listing_id: `demo-${gameId}-${key}-${i}`,
      price_per_seat_cents: i === 0 ? p.minPriceCents : price,
      quantity_available: qty,
      quantity_sold: 0,
      listing_note: r() < 0.3 ? NOTES[Math.floor(r() * NOTES.length)]! : null,
      game_id: gameId,
      seat_block_id: `demo-block-${gameId}-${key}-${i}`,
      section: key,
      row: String(2 + Math.floor(r() * ROW_MAX[level])),
      seats_json: JSON.stringify(seats),
      section_type: p.sectionType,
      seat_view_photo_url: null,
      opponent: g?.opponent ?? 'Sample opponent',
      game_date: g?.game_date ?? '2026-09-13',
      kickoff_time: g?.kickoff_time ?? '13:00',
      week_number: g?.week_number ?? null,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Browse-page generation + client-side filtering
// ---------------------------------------------------------------------------

export type DemoFilter = {
  game_id: string;
  section_types: string[];
  section: string;
  min_price: string;
  max_price: string;
  min_qty: string;
  sort: 'price_asc' | 'price_desc' | 'section';
};

const BROWSE_CAP = 120;

export function demoListingsForBrowse(filters: DemoFilter, games: Game[]): Listing[] {
  const pool = filters.game_id
    ? games.filter((g) => g.game_id === filters.game_id)
    : games;
  const all: Listing[] = [];
  for (const game of pool) {
    for (const id of ALL_SECTION_IDS) {
      all.push(...demoListingsFor(game.game_id, id, game));
    }
  }
  const sectionQ = filters.section.trim().toUpperCase();
  const min = filters.min_price ? Number(filters.min_price) * 100 : null;
  const max = filters.max_price ? Number(filters.max_price) * 100 : null;
  const minQty = filters.min_qty ? Number(filters.min_qty) : null;
  const filtered = all.filter((l) => {
    if (filters.section_types.length && !filters.section_types.includes(l.section_type)) return false;
    if (sectionQ && !l.section.startsWith(sectionQ)) return false;
    if (min !== null && l.price_per_seat_cents < min) return false;
    if (max !== null && l.price_per_seat_cents > max) return false;
    if (minQty !== null && l.quantity_available - l.quantity_sold < minQty) return false;
    return true;
  });
  filtered.sort((a, b) => {
    if (filters.sort === 'price_desc') return b.price_per_seat_cents - a.price_per_seat_cents;
    if (filters.sort === 'section') return a.section.localeCompare(b.section, undefined, { numeric: true });
    return a.price_per_seat_cents - b.price_per_seat_cents;
  });
  return filtered.slice(0, BROWSE_CAP);
}
